import dayjs from 'dayjs';
import { t } from 'i18next';
import {
  RouteAvailabilityFormSchema,
  RouteAvailabilitySchema,
} from 'pos-shared/models/routeSchema';
import { z } from 'zod';

const alphanumericRegex = /^[a-zA-Z0-9_]*$/;

export const RouteBasicDetailsSchema = z.object({
  name: z
    .string()
    .min(1, { error: t('routes.validationErrors.nameCannotBeEmpty') })
    .regex(alphanumericRegex, {
      error: t('routes.validationErrors.nameCannotContainSpecialCharacters'),
    }),
  description: z
    .string()
    .min(1, { error: t('routes.validationErrors.descriptionCannotBeEmpty') }),
  sameDay: z.boolean().optional(),
});

export const RouteAvailabilityFormRefinedSchema =
  RouteAvailabilityFormSchema.refine((data) => data.daysOfTheWeek?.length > 0, {
    error: t('common.formErrors.required'),
    path: ['daysOfTheWeek'],
    when(payload) {
      return RouteAvailabilityFormSchema.pick({
        daysOfTheWeek: true,
      }).safeParse(payload.value).success;
    },
  })
    .refine(
      (data) =>
        dayjs(data.startTimeWindow.endTime).isSameOrAfter(
          dayjs(data.startTimeWindow.startTime)
        ),
      {
        error: t(
          'routes.validationErrors.earliestStartTimeMustBeSameOrBeforeLatestStartTime'
        ),
        path: ['startTimeWindow', 'startTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            startTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.finishTimeWindow.endTime).isSameOrAfter(
          dayjs(data.finishTimeWindow.startTime)
        ),
      {
        error: t(
          'routes.validationErrors.earliestEndTimeMustBeSameOrBeforeLatestEndTime'
        ),
        path: ['finishTimeWindow', 'startTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            finishTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.serviceTimeWindow.endTime).isSameOrAfter(
          dayjs(data.serviceTimeWindow.startTime)
        ),
      {
        error: t(
          'routes.validationErrors.earliestServiceTimeMustBeSameOrBeforeLatestServiceTime'
        ),
        path: ['serviceTimeWindow', 'startTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            serviceTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.startTimeWindow.startTime).isSameOrBefore(
          dayjs(data.serviceTimeWindow.startTime)
        ),
      {
        error: t(
          'routes.validationErrors.earliestStartTimeMustBeSameOrBeforeEarliestServiceStartTime'
        ),
        path: ['startTimeWindow', 'startTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            startTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.serviceTimeWindow.startTime).isSameOrAfter(
          dayjs(data.startTimeWindow.startTime)
        ),
      {
        error: t(
          'routes.validationErrors.earliestServiceTimeMustBeSameOrAfterEarliestStartTime'
        ),
        path: ['serviceTimeWindow', 'startTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            serviceTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.serviceTimeWindow.endTime).isBefore(
          dayjs(data.finishTimeWindow.endTime)
        ),
      {
        error: t(
          'routes.validationErrors.latestServiceTimeMustBeBeforeLatestEndTime'
        ),
        path: ['serviceTimeWindow', 'endTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            serviceTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.finishTimeWindow.endTime).isAfter(
          dayjs(data.serviceTimeWindow.endTime)
        ),
      {
        error: t(
          'routes.validationErrors.latestEndTimeMustBeAfterLatestServiceTime'
        ),
        path: ['finishTimeWindow', 'endTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            finishTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    )
    .refine(
      (data) =>
        dayjs(data.serviceTimeWindow.startTime).isSameOrAfter(
          dayjs(data.startTimeWindow.endTime)
        ),
      {
        error: t(
          'routes.validationErrors.earliestServiceTimeMustBeSameOrAfterLatestStartTime'
        ),
        path: ['serviceTimeWindow', 'startTime'],
        when(payload) {
          return RouteAvailabilityFormSchema.pick({
            serviceTimeWindow: true,
          }).safeParse(payload.value).success;
        },
      }
    );

export const RouteRechargeSettingsSchema = z
  .object({
    rechargeSettings: z.object({
      allowRecharges: z.boolean().optional(),
      rechargePolicy: z.string().optional(),
      rechargeToCapacity: z.boolean().optional(),
    }),
    settings: z.object({
      limits: z.object({
        maxDistance: z.number().optional(),
      }),
    }),
  })
  .refine(
    (data) =>
      !data.rechargeSettings.allowRecharges ||
      (data.settings.limits.maxDistance !== undefined &&
        data.settings.limits.maxDistance >= 100000),
    {
      error: t(
        'routes.validationErrors.maxDistanceMustBeGreaterOrEqualTo100000'
      ),
      path: ['settings', 'limits', 'maxDistance'],
    }
  );

export const RouteCapacitySchema = z.object({
  capabilities: z
    .string()
    .array()
    .min(1, {
      error: t(
        'routes.validationErrors.capabilitiesMustContainAtLeastOneElement'
      ),
    })
    .refine(
      (data) => data?.join().length < 255,
      t(
        'routes.validationErrors.combinedCapabilitiesCharactersLengthMustBeLessThan255Characters'
      )
    ),
  capacitySettings: z.object({
    measure1: z.number().nonnegative(),
    measure2: z.number().nonnegative(),
    measure3: z.number().nonnegative(),
    measure4: z.number().nonnegative(),
  }),
});

export type RouteBasicDetailsModel = z.infer<typeof RouteBasicDetailsSchema>;
export type RouteAvailabilityModel = z.infer<typeof RouteAvailabilitySchema>;
export type RouteRechargeSettingsModel = z.infer<
  typeof RouteRechargeSettingsSchema
>;
export type RouteCapacityModel = z.infer<typeof RouteCapacitySchema>;
export type RouteAvailabilityFormRefinedModel = z.infer<
  typeof RouteAvailabilityFormSchema
>;
